import { useCallback, useEffect, useRef } from 'react'

import { useAppointmentStore } from '@/store.ts'
import {
  formComplete,
  formError,
  formInteraction,
  formProgress,
  formStart,
} from '@/utils/gtm.ts'
import {
  clearSessionStorage,
  storageFormStartLogged,
  storageFormStepIndex,
  storageFormStepName,
} from '@/utils/sessionStorage.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'
import { XpertFormStep } from '@/utils/XpertFormSteps.ts'

export type FormStep = XpertFormStep | VelthuisFormStep

const getFormStepIndex = (formStepName: FormStep) => {
  const previousFormStepName = storageFormStepName.get()
  const previousFormStepIndex = storageFormStepIndex.get()

  if (!previousFormStepName) {
    return 1
  }
  if (previousFormStepName === formStepName) {
    return previousFormStepIndex
  }
  return previousFormStepIndex + 1
}

type TrackFormOptions = {
  complete?: boolean
  manual?: boolean
  loadTime?: number
}

export const useTrackFormProgress = (
  formStepName: FormStep,
  { complete, loadTime, manual }: TrackFormOptions = {},
) => {
  const {
    appointmentType,
    patientData: { email } = { email: '' },
    selectedLocation,
    selectedSlot,
  } = useAppointmentStore.getState()

  const previousFormStepNameRef = useRef(storageFormStepName.get())
  const formStepIndexRef = useRef(getFormStepIndex(formStepName))

  const commonFormOptions = {
    clinic: selectedLocation?.name
      ? selectedLocation.name
      : selectedSlot?.location?.name,
    consultationType: appointmentType,
    doctor: selectedSlot?.practitioner?.name,
    email,
  }

  const trackFormProgress = useCallback(() => {
    const hasStarted = storageFormStartLogged.get()

    const formOptions = {
      ...commonFormOptions,
      formStepIndex: formStepIndexRef.current,
      formStepName,
      loadTime,
      previousFormStepName: previousFormStepNameRef.current,
    }
    if (complete) {
      clearFormProgress()
      formComplete(formOptions)
    } else {
      if (!hasStarted) {
        storageFormStartLogged.set()
        formStart(formOptions)
      } else {
        formProgress(formOptions)
      }
      storageFormStepName.set(formStepName)
      storageFormStepIndex.set(formStepIndexRef.current)
    }
  }, [formStepName, complete, previousFormStepNameRef])

  useEffect(() => {
    if (manual) {
      return
    }
    trackFormProgress()
  }, [manual])

  const trackFormInteraction = useCallback(
    ({
      action,
      formFieldName,
      formFieldValue = '',
      formFieldValues = [],
    }: {
      action: 'click' | 'open' | 'close' | 'next' | 'previous'
      formFieldName: string
      formFieldValue?: string
      formFieldValues?: string[]
    }) => {
      formInteraction({
        action,
        formFieldName,
        formFieldValue,
        formFieldValues,
        formStepIndex: formStepIndexRef.current,
        formStepName,
        previousFormStepName: previousFormStepNameRef.current,
        ...commonFormOptions,
      })
    },
    [formStepName],
  )

  const trackFormError = useCallback(
    ({ error }: { error: string }) => {
      const formOptions = {
        ...commonFormOptions,
        formStepIndex: formStepIndexRef.current,
        formStepName,
        previousFormStepName: previousFormStepNameRef.current,
      }

      formError({
        error,
        ...formOptions,
      })
    },
    [formStepName],
  )

  const triggerManualFormProgress = useCallback(() => {
    previousFormStepNameRef.current = storageFormStepName.get()
    trackFormProgress()
  }, [formStepName])

  return {
    trackFormError,
    trackFormInteraction,
    triggerManualFormProgress,
  }
}

export const useTrackFormError = ({ error }: { error: string }) => {
  useEffect(() => {
    const formStepName = storageFormStepName.get()
    const formStepIndex = storageFormStepIndex.get()
    const { patientData } = useAppointmentStore.getState()

    formError({
      email: patientData?.email,
      error,
      formStepIndex,
      formStepName,
    })
  }, [])
}

export const clearFormProgress = () => {
  clearSessionStorage()
}
